// Since statuses come back as strings, these are used to give the statuses
// some semantic value that can be presented in different modes within the UI.
const AED_STATUS = {
  FAILURE: -1,
  PENDING: 0,
  SUCCESS: 1,
};

export const AED_STATUS_MODE = {
  STANDALONE: 'standalone',
  EVENT: 'event',
  NONE: 'none',
};

// Used to display a start state for Avive search, not an official avive status
export const AVIVE_STATUS_REQUEST = 'Contacting Avive...';
export const AVIVE2_STATUS_CANCELING = 'Canceling AED Dispatch.';
export const AVIVE2_STATUS_NOTE_SENT = 'Note Sent.';
export const AVIVE2_STATUS_UNABLE_TO_SEND_NOTE = 'Unable to send note.';
export const AVIVE2_STATUS_CANCELLATION_FAILED =
  'Cancellation of the AED Dispatch has failed.';
export const AVIVE2_STATUS_DUPLICATE_REQUEST_ERROR = 'Duplicate Request (409).';

// These are strings that are sent in Avive payloads and are the only values
// that indicate a status change for an Avive request.
export const AVIVE_STATUS_SEARCHING = 'Searching for nearby devices...';
export const AVIVE_STATUS_USER_ACCEPTED = 'App Users accepted';
export const AVIVE_STATUS_DEVICE_ACCEPTED = 'Device(s) accepted';
export const AVIVE_STATUS_DEVICE_FOUND = 'Device(s) found, not accepted yet';
export const AVIVE_STATUS_DEVICE_NOT_FOUND = 'No Devices found';

// Phase 2 specific statuses
export const AVIVE2_STATUS_NO_AED_FOUND_NOT_APP_ACCEPTED =
  'No AED devices found, No App Responders Accepted';
export const AVIVE2_STATUS_DEVICES_NEARBY_NOT_APP_ACCEPTED =
  'AED Devices nearby, No App Responders Accepted';
export const AVIVE2_STATUS_DEVICE_ACCEPTED_NOT_APP_ACCEPTED =
  'AED Devices Accepted, No App Responders Accepted';
export const AVIVE2_STATUS_NO_AED_FOUND_APP_ACCEPTED =
  'No AED Devices Found, App Responders Accepted';
export const AVIVE2_STATUS_AED_NEARBY_APP_ACCEPTED =
  'AED Devices nearby, App Responders Accepted';
export const AVIVE2_STATUS_AED_ACCEPTED_APP_ACCEPTED =
  'AED Devices Accepted, App Responders Accepted';
export const AVIVE2_STATUS_DEVICE_ACTIVATED = 'Device Activated';
export const AVIVE2_STATUS_AED_CANCELED = 'AED Dispatch Canceled';
export const AVIVE2_STATUS_AED_CONCLUDED = 'Device Usage Concluded';
export const AVIVE2_STATUS_EMERGENCY_TIMEOUT = 'Emergency Timeout';

const AVIVE2 = {
  NO_AED_FOUND_NOT_APP_ACCEPTED: {
    type: AED_STATUS.PENDING,
    message: AVIVE2_STATUS_NO_AED_FOUND_NOT_APP_ACCEPTED,
    mode: AED_STATUS_MODE.EVENT,
  },
  DEVICES_NEARBY_NOT_APP_ACCEPTED: {
    type: AED_STATUS.PENDING,
    message: AVIVE2_STATUS_DEVICES_NEARBY_NOT_APP_ACCEPTED,
    mode: AED_STATUS_MODE.EVENT,
  },
  DEVICE_ACCEPTED_NOT_APP_ACCEPTED: {
    type: AED_STATUS.PENDING,
    message: AVIVE2_STATUS_DEVICE_ACCEPTED_NOT_APP_ACCEPTED,
    mode: AED_STATUS_MODE.EVENT,
  },
  NO_AED_FOUND_APP_ACCEPTED: {
    type: AED_STATUS.PENDING,
    message: AVIVE2_STATUS_NO_AED_FOUND_APP_ACCEPTED,
    mode: AED_STATUS_MODE.EVENT,
  },
  AED_NEARBY_APP_ACCEPTED: {
    type: AED_STATUS.PENDING,
    message: AVIVE2_STATUS_AED_NEARBY_APP_ACCEPTED,
    mode: AED_STATUS_MODE.EVENT,
  },
  AED_ACCEPTED_APP_ACCEPTED: {
    type: AED_STATUS.PENDING,
    message: AVIVE2_STATUS_AED_ACCEPTED_APP_ACCEPTED,
    mode: AED_STATUS_MODE.EVENT,
  },
  DEVICE_ACTIVATED: {
    type: AED_STATUS.SUCCESS,
    message: AVIVE2_STATUS_DEVICE_ACTIVATED,
    mode: AED_STATUS_MODE.STANDALONE,
  },
  CANCELING: {
    type: AED_STATUS.PENDING,
    message: AVIVE2_STATUS_CANCELING,
    mode: AED_STATUS_MODE.STANDALONE,
  },
  AED_CANCELED: {
    type: AED_STATUS.FAILURE,
    message: AVIVE2_STATUS_AED_CANCELED,
    mode: AED_STATUS_MODE.STANDALONE,
  },
  AED_CONCLUDED: {
    type: AED_STATUS.FAILURE,
    message: AVIVE2_STATUS_AED_CONCLUDED,
  },
  NOTE_SENT: {
    type: AED_STATUS.SUCCESS,
    mode: AED_STATUS_MODE.STANDALONE,
    message: AVIVE2_STATUS_NOTE_SENT,
  },
  UNABLE_TO_SEND_NOTE: {
    type: AED_STATUS.FAILURE,
    mode: AED_STATUS_MODE.STANDALONE,
    message: AVIVE2_STATUS_UNABLE_TO_SEND_NOTE,
  },
  CANCELLATION_FAILED: {
    type: AED_STATUS.FAILURE,
    mode: AED_STATUS_MODE.STANDALONE,
    message: AVIVE2_STATUS_CANCELLATION_FAILED,
  },
  DUPLICATE_REQUEST_ERROR: {
    type: AED_STATUS.FAILURE,
    mode: AED_STATUS_MODE.STANDALONE,
    message: AVIVE2_STATUS_DUPLICATE_REQUEST_ERROR,
  },
  EMERGENCY_TIMEOUT: {
    type: AED_STATUS.FAILURE,
    mode: AED_STATUS_MODE.STANDALONE,
    message: AVIVE2_STATUS_EMERGENCY_TIMEOUT,
  },
};

// Error messages by specific status codes.
export const AVIVE_STATUS_FAILED_TO_CONTACT = 'Failed to Contact Avive (503).';
export const AVIVE_STATUS_CONTACT_TIMEOUT = 'Failed to Contact Avive (504).';

export const AVIVE_STATUS_SUCCESS = 'Device Activated';
export const AVIVE_STATUS_FAILURE = 'Failed to trigger Avive AED'; // generic failure

// These are used for the status within the info window.
export const AVIVE_ACTIONABLE_STATUS_ACCEPTED = 'Accepted and En Route';
export const AVIVE_ACTIONABLE_STATUS_UNACCEPTED = 'Available for Pickup';

// Because the data has no status codes to program against, the types assigned
// here help the UI distinguish between them semantically. These are all
// statuses that are passthroughed originating from the Avive service.
export const AVIVE_STATUSES = {
  [AVIVE_STATUS_REQUEST.toLowerCase()]: {
    type: AED_STATUS.PENDING,
    message: AVIVE_STATUS_REQUEST,
  },
  [AVIVE_STATUS_SEARCHING.toLowerCase()]: {
    type: AED_STATUS.PENDING,
    message: AVIVE_STATUS_SEARCHING,
  },
  [AVIVE_STATUS_DEVICE_FOUND.toLowerCase()]: {
    type: AED_STATUS.PENDING,
    message: AVIVE_STATUS_DEVICE_FOUND,
  },
  [AVIVE_STATUS_DEVICE_ACCEPTED.toLowerCase()]: {
    type: AED_STATUS.PENDING,
    message: AVIVE_STATUS_DEVICE_ACCEPTED,
  },
  [AVIVE_STATUS_USER_ACCEPTED.toLowerCase()]: {
    type: AED_STATUS.PENDING,
    message: AVIVE_STATUS_USER_ACCEPTED,
  },
  [AVIVE_STATUS_DEVICE_NOT_FOUND.toLowerCase()]: {
    type: AED_STATUS.FAILURE,
    message: AVIVE_STATUS_DEVICE_NOT_FOUND,
  },
  [AVIVE_STATUS_FAILED_TO_CONTACT.toLowerCase()]: {
    type: AED_STATUS.FAILURE,
    message: AVIVE_STATUS_FAILED_TO_CONTACT,
    mode: AED_STATUS_MODE.STANDALONE,
  },
  [AVIVE_STATUS_CONTACT_TIMEOUT.toLowerCase()]: {
    type: AED_STATUS.FAILURE,
    message: AVIVE_STATUS_CONTACT_TIMEOUT,
    mode: AED_STATUS_MODE.STANDALONE,
  },
  [AVIVE_STATUS_FAILURE.toLowerCase()]: {
    type: AED_STATUS.FAILURE,
    message: AVIVE_STATUS_FAILURE,
    mode: AED_STATUS_MODE.STANDALONE,
  },
  [AVIVE_STATUS_SUCCESS.toLowerCase()]: {
    type: AED_STATUS.SUCCESS,
    message: AVIVE_STATUS_SUCCESS,
  },
};

// Patch in new Avive Phase 2 statuses.
Object.keys(AVIVE2).forEach(key => {
  const statusInfo = AVIVE2[key] || {};
  const { message = '' } = statusInfo;
  AVIVE_STATUSES[message.toLowerCase()] = {
    ...statusInfo,
  };
});

/**
 * Returns an Avive Status from a case insensitive status string.
 * To be used in cases where casing of the status might be unreliable.
 *
 * @callback onErrorCallback
 * @param {Error}
 *
 * @param {string} status
 * @param {onErrorCallback} onError - an optional error callback
 */
export const findAviveStatus = (status = '', onError = () => {}) => {
  if (typeof status === 'string') {
    const aviveStatus = AVIVE_STATUSES[status.toLowerCase()];
    if (!aviveStatus) {
      onError(new Error(`Unrecognized Avive status: ${status}`));
    }
    return aviveStatus;
  }
};

/**
 * Returns an Actionable Avive Status, which is a human readable
 * message that is used within the info window for the spatial object.
 * @param {boolean|string} isAEDAccepted
 * NOTE this is being coerced and will be fixed at a later time by BE.
 * TODO Remove the logic that was coerced after BE change is in
 * https://github.com/RapidSOS/milky-way/projects/1#card-74924573
 */
export const findAviveActionableStatus = isAEDAccepted => {
  let coercedValue = isAEDAccepted;
  if (typeof isAEDAccepted === 'string') {
    coercedValue = !!(isAEDAccepted.toLowerCase() === 'true');
  }
  return coercedValue
    ? AVIVE_ACTIONABLE_STATUS_ACCEPTED
    : AVIVE_ACTIONABLE_STATUS_UNACCEPTED;
};

export default AED_STATUS;
